import { useState } from 'react';

import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { postAppAccountPasswordresetInvoke } from '@/api';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import { Loader } from '@/components/Loader/loader';
import TextInput from '@/components/TextInput/TextInput';
import { ROUTE } from '@/constants/route';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';
import checkEmailFormat from '@/utils/functions/checkEmailFormat/checkEmailFormat';
import { log } from '@/utils/functions/logger/logger';

import styles from './styles.module.css';

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>('');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isErrorMail, setIsErrorMail] = useState(false);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Connexion', path: ROUTE.LOGIN },
    { label: 'Mot de passe oublié', path: ROUTE.RESETPASSWORD },
  ];

  const mutation = useMutation({
    mutationFn: (email: string) => postAppAccountPasswordresetInvoke({ email }),
    onSuccess: () => {
      navigate(ROUTE.REDIRECTIONMAIL, { state: { email: email } });
    },
    onError: (error) => {
      log.error(`ResetPassword mutation failed: ${error}`);
      navigate(ROUTE.ERREUR);
    },
  });

  const validateMail = () => {
    if (email === '') {
      setIsErrorMail(true);
      setErrorText('Champs requis');
      return false;
    } else if (checkEmailFormat(email) === false) {
      setIsErrorMail(true);
      setErrorText('Veuillez saisir une adresse mail valide');
      return false;
    } else {
      setIsErrorMail(false);
      setErrorText(null);
      return true;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateMail()) {
      localStorage.setItem('email', email);
      mutation.mutate(email);
    }
  };

  return (
    <div className={styles.resetPassword}>
      {mutation.isPending && <Loader />}

      <MtxGrid row>
        <MtxGrid col colLg={6} offsetLg={3} offsetSm={2} colSm={6} colXs={12}>
          <Breadcrumb items={breadcrumbItems} alternative />
        </MtxGrid>
      </MtxGrid>

      <MtxGrid row>
        <MtxGrid col colLg={6} offsetLg={3} offsetSm={2} colSm={6} colXs={12}>
          <div className={styles.titleResetPassword}>
            <h1>{t('resetPassword.label')}</h1>
          </div>
        </MtxGrid>
      </MtxGrid>
      <MtxGrid row>
        <MtxGrid col colLg={6} offsetLg={3} offsetSm={2} colSm={8} colXs={12}>
          <div className={styles.descriptionEmail}>
            <p>{t('resetPassword.description')}</p>
          </div>
        </MtxGrid>
      </MtxGrid>
      <form
        onSubmit={(e: React.FormEvent) => {
          handleSubmit(e);
        }}
      >
        <MtxGrid row>
          <MtxGrid col colLg={6} offsetLg={3} offsetSm={2} colSm={8} colXs={12}>
            <TextInput
              label="Email"
              labelFor="email"
              placeholder="Votre adresse mail"
              type="email"
              value={email}
              textError={errorText}
              isError={isErrorMail}
              required={true}
              onChange={(e) => {
                setIsErrorMail(false);
                setEmail(e.currentTarget.value);
              }}
            />
          </MtxGrid>
        </MtxGrid>
        <MtxGrid row>
          <MtxGrid col colLg={6} offsetLg={3} offsetSm={2} colSm={8} colXs={12}>
            <div className={styles.buttonConnexionReset}>
              <MtxButton onClick={handleSubmit} variant="primary" size="large" fullWidth>
                <div>{t('resetPassword.button')}</div>
              </MtxButton>
            </div>
          </MtxGrid>
        </MtxGrid>

        <input className="hide-element" type="submit" />
      </form>
    </div>
  );
};
